// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Button,
  Icon,
  Input,
  Segment,
  Header,
  Message,
  Grid,
  List,
  Checkbox,
  Label,
  Loader,
} from 'semantic-ui-react';
import { supportStaffApi } from '../api/supportStaffApi';
import { advisorApi } from '../api/advisorApi';
import type { AdvisorForAssignment } from '../types/supportStaff';

interface AssignmentManagementModalProps {
  open: boolean;
  supportStaffId: string;
  supportStaffName: string;
  brokerageId: string;
  onClose: () => void;
  onSuccess: () => void;
}

function AssignmentManagementModal({
  open,
  supportStaffId,
  supportStaffName,
  brokerageId,
  onClose,
  onSuccess,
}: Readonly<AssignmentManagementModalProps>) {
  const [availableAdvisors, setAvailableAdvisors] = useState<AdvisorForAssignment[]>([]);
  const [assignedAdvisors, setAssignedAdvisors] = useState<AdvisorForAssignment[]>([]);
  const [selectedToAssign, setSelectedToAssign] = useState<Set<string>>(new Set());
  const [searchAvailable, setSearchAvailable] = useState('');
  const [searchAssigned, setSearchAssigned] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pending changes tracking
  const [pendingAssignments, setPendingAssignments] = useState<string[]>([]);
  const [pendingUnassignments, setPendingUnassignments] = useState<string[]>([]);

  const loadAdvisors = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Get all advisors in the brokerage
      const response = await advisorApi.getByBrokerage(brokerageId, 0, 100);
      const allAdvisors = response.content.filter(
        (a) => a.status === 'active' && (a.role === 'ADMIN' || a.role === 'USER')
      );

      // Get assigned advisors
      const assigned = await supportStaffApi.getAssignedAdvisors(supportStaffId);
      const assignedIds = new Set(assigned.map((a) => a.id));

      // Split into available and assigned
      const available: AdvisorForAssignment[] = [];
      const currentlyAssigned: AdvisorForAssignment[] = [];

      for (const advisor of allAdvisors) {
        const advisorForAssignment: AdvisorForAssignment = {
          id: advisor.id!,
          firstName: advisor.firstName,
          lastName: advisor.lastName,
          email: advisor.email,
          photoUrl: advisor.photoUrl,
          profileCount: 0, // Would need separate API call for profile counts
          isAssigned: assignedIds.has(advisor.id!),
        };

        if (assignedIds.has(advisor.id!)) {
          // Find the assigned advisor data with profile count
          const assignedData = assigned.find((a) => a.id === advisor.id);
          if (assignedData) {
            advisorForAssignment.profileCount = assignedData.profileCount;
          }
          currentlyAssigned.push(advisorForAssignment);
        } else {
          available.push(advisorForAssignment);
        }
      }

      setAvailableAdvisors(available);
      setAssignedAdvisors(currentlyAssigned);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load advisors');
    } finally {
      setLoading(false);
    }
  }, [brokerageId, supportStaffId]);

  useEffect(() => {
    if (open) {
      loadAdvisors();
      // Reset state
      setSelectedToAssign(new Set());
      setPendingAssignments([]);
      setPendingUnassignments([]);
      setSearchAvailable('');
      setSearchAssigned('');
    }
  }, [open, loadAdvisors]);

  const handleAssign = (advisorId: string) => {
    const advisor = availableAdvisors.find((a) => a.id === advisorId);
    if (!advisor) return;

    // Move from available to assigned (locally)
    setAvailableAdvisors((prev) => prev.filter((a) => a.id !== advisorId));
    setAssignedAdvisors((prev) => [...prev, { ...advisor, isAssigned: true }]);
    setPendingAssignments((prev) => [...prev, advisorId]);
    setPendingUnassignments((prev) => prev.filter((id) => id !== advisorId));
    setSelectedToAssign((prev) => {
      const next = new Set(prev);
      next.delete(advisorId);
      return next;
    });
  };

  const handleUnassign = (advisorId: string) => {
    const advisor = assignedAdvisors.find((a) => a.id === advisorId);
    if (!advisor) return;

    // Move from assigned to available (locally)
    setAssignedAdvisors((prev) => prev.filter((a) => a.id !== advisorId));
    setAvailableAdvisors((prev) => [...prev, { ...advisor, isAssigned: false }]);
    setPendingUnassignments((prev) => [...prev, advisorId]);
    setPendingAssignments((prev) => prev.filter((id) => id !== advisorId));
  };

  const handleAssignSelected = () => {
    selectedToAssign.forEach((id) => handleAssign(id));
  };

  const handleSelectAll = () => {
    const filtered = getFilteredAvailable();
    setSelectedToAssign(new Set(filtered.map((a) => a.id)));
  };

  const handleClearSelection = () => {
    setSelectedToAssign(new Set());
  };

  const handleSave = async () => {
    if (pendingAssignments.length === 0 && pendingUnassignments.length === 0) {
      onSuccess();
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Process unassignments first
      for (const advisorId of pendingUnassignments) {
        await supportStaffApi.unassignAdvisor(supportStaffId, brokerageId, advisorId);
      }

      // Process assignments
      if (pendingAssignments.length > 0) {
        await supportStaffApi.assignAdvisors(supportStaffId, brokerageId, {
          advisorIds: pendingAssignments,
        });
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Discard all pending changes
    setPendingAssignments([]);
    setPendingUnassignments([]);
    onClose();
  };

  const getFilteredAvailable = () => {
    if (!searchAvailable) return availableAdvisors;
    const search = searchAvailable.toLowerCase();
    return availableAdvisors.filter(
      (a) =>
        a.firstName.toLowerCase().includes(search) ||
        a.lastName.toLowerCase().includes(search) ||
        a.email.toLowerCase().includes(search)
    );
  };

  const getFilteredAssigned = () => {
    if (!searchAssigned) return assignedAdvisors;
    const search = searchAssigned.toLowerCase();
    return assignedAdvisors.filter(
      (a) =>
        a.firstName.toLowerCase().includes(search) ||
        a.lastName.toLowerCase().includes(search) ||
        a.email.toLowerCase().includes(search)
    );
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const totalProfileCount = assignedAdvisors.reduce((sum, a) => sum + a.profileCount, 0);
  const hasChanges = pendingAssignments.length > 0 || pendingUnassignments.length > 0;

  const filteredAvailable = getFilteredAvailable();
  const filteredAssigned = getFilteredAssigned();

  return (
    <Modal open={open} onClose={handleCancel} size="large" closeIcon>
      <Modal.Header>
        <Icon name="exchange" /> Manage Advisor Assignments
      </Modal.Header>
      <Modal.Content>
        <p style={{ marginBottom: '1em' }}>
          For: <strong>{supportStaffName}</strong>
        </p>

        {error && (
          <Message negative onDismiss={() => setError(null)}>
            <Message.Header>Error</Message.Header>
            <p>{error}</p>
          </Message>
        )}

        {loading ? (
          <Segment placeholder>
            <Loader active inline="centered">
              Loading advisors...
            </Loader>
          </Segment>
        ) : (
          <Grid columns={2} divided>
            {/* Available Advisors Panel */}
            <Grid.Column>
              <Segment>
                <Header as="h4">
                  Available Advisors ({availableAdvisors.length})
                </Header>
                <Input
                  icon="search"
                  placeholder="Search advisors..."
                  fluid
                  value={searchAvailable}
                  onChange={(e) => setSearchAvailable(e.target.value)}
                  style={{ marginBottom: '1em' }}
                />

                <div
                  style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid #f0f0f0',
                    borderRadius: '4px',
                  }}
                >
                  {filteredAvailable.length === 0 ? (
                    <Message info size="small">
                      <Message.Content>No available advisors</Message.Content>
                    </Message>
                  ) : (
                    <List divided selection>
                      {filteredAvailable.map((advisor) => (
                        <List.Item
                          key={advisor.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0.75em',
                          }}
                        >
                          <Checkbox
                            checked={selectedToAssign.has(advisor.id)}
                            onChange={() => {
                              setSelectedToAssign((prev) => {
                                const next = new Set(prev);
                                if (next.has(advisor.id)) {
                                  next.delete(advisor.id);
                                } else {
                                  next.add(advisor.id);
                                }
                                return next;
                              });
                            }}
                            style={{ marginRight: '1em' }}
                          />
                          <div
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: '#2185D0',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.8em',
                              fontWeight: 'bold',
                              marginRight: '0.75em',
                              flexShrink: 0,
                            }}
                          >
                            {advisor.photoUrl ? (
                              <img
                                src={advisor.photoUrl}
                                alt=""
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                }}
                              />
                            ) : (
                              getInitials(advisor.firstName, advisor.lastName)
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {advisor.firstName} {advisor.lastName}
                            </div>
                            <div
                              style={{
                                fontSize: '0.85em',
                                color: '#666',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {advisor.email}
                            </div>
                          </div>
                          <Button
                            icon="plus"
                            color="green"
                            size="mini"
                            onClick={() => handleAssign(advisor.id)}
                          />
                        </List.Item>
                      ))}
                    </List>
                  )}
                </div>

                <div
                  style={{
                    marginTop: '1em',
                    display: 'flex',
                    gap: '0.5em',
                    flexWrap: 'wrap',
                  }}
                >
                  <Button
                    size="small"
                    onClick={handleSelectAll}
                    disabled={filteredAvailable.length === 0}
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    onClick={handleClearSelection}
                    disabled={selectedToAssign.size === 0}
                  >
                    Clear
                  </Button>
                </div>
              </Segment>
            </Grid.Column>

            {/* Assigned Advisors Panel */}
            <Grid.Column>
              <Segment>
                <Header as="h4">
                  Assigned Advisors ({assignedAdvisors.length})
                </Header>
                <Input
                  icon="search"
                  placeholder="Search assigned..."
                  fluid
                  value={searchAssigned}
                  onChange={(e) => setSearchAssigned(e.target.value)}
                  style={{ marginBottom: '1em' }}
                />

                <div
                  style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid #f0f0f0',
                    borderRadius: '4px',
                  }}
                >
                  {filteredAssigned.length === 0 ? (
                    <Message info size="small">
                      <Message.Content>No assigned advisors</Message.Content>
                    </Message>
                  ) : (
                    <List divided selection>
                      {filteredAssigned.map((advisor) => (
                        <List.Item
                          key={advisor.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0.75em',
                          }}
                        >
                          <div
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: '#00B5AD',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.8em',
                              fontWeight: 'bold',
                              marginRight: '0.75em',
                              flexShrink: 0,
                            }}
                          >
                            {advisor.photoUrl ? (
                              <img
                                src={advisor.photoUrl}
                                alt=""
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                }}
                              />
                            ) : (
                              getInitials(advisor.firstName, advisor.lastName)
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {advisor.firstName} {advisor.lastName}
                              {pendingAssignments.includes(advisor.id) && (
                                <Label size="mini" color="green" style={{ marginLeft: '0.5em' }}>
                                  New
                                </Label>
                              )}
                            </div>
                            <div
                              style={{
                                fontSize: '0.85em',
                                color: '#666',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {advisor.email}
                            </div>
                          </div>
                          <Label size="small">{advisor.profileCount}</Label>
                          <Button
                            icon="minus"
                            color="red"
                            size="mini"
                            onClick={() => handleUnassign(advisor.id)}
                            style={{ marginLeft: '0.5em' }}
                          />
                        </List.Item>
                      ))}
                    </List>
                  )}
                </div>

                <div
                  style={{
                    marginTop: '1em',
                    padding: '0.75em',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                  }}
                >
                  <strong>Total Profiles:</strong> {totalProfileCount}
                </div>
              </Segment>
            </Grid.Column>
          </Grid>
        )}

        {hasChanges && (
          <Message warning style={{ marginTop: '1em' }}>
            <Message.Content>
              <Icon name="info circle" />
              You have unsaved changes:{' '}
              {pendingAssignments.length > 0 && (
                <span>
                  {pendingAssignments.length} advisor
                  {pendingAssignments.length === 1 ? '' : 's'} to assign
                </span>
              )}
              {pendingAssignments.length > 0 && pendingUnassignments.length > 0 && ', '}
              {pendingUnassignments.length > 0 && (
                <span>
                  {pendingUnassignments.length} advisor
                  {pendingUnassignments.length === 1 ? '' : 's'} to unassign
                </span>
              )}
            </Message.Content>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        {selectedToAssign.size > 0 && (
          <Button color="green" onClick={handleAssignSelected} floated="left">
            <Icon name="plus" /> Assign Selected ({selectedToAssign.size})
          </Button>
        )}
        <Button onClick={handleCancel} disabled={saving}>
          Cancel
        </Button>
        <Button primary onClick={handleSave} loading={saving} disabled={loading}>
          <Icon name="checkmark" /> Save Changes
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

export default AssignmentManagementModal;
